import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { CompanyRow } from '@/lib/types'
import { Badge, Button, Field, Input, PageSpinner, Textarea, useConfirm, useToast } from '@/components/ui'
import AllocationEditor from '@/components/company-admin/AllocationEditor'
import RoomingListTab from '@/components/company-admin/RoomingListTab'
import AccessTab from '@/components/company-admin/AccessTab'
import ActivityTab from '@/components/company-admin/ActivityTab'
import CompanyImportExportTab from '@/components/company-admin/ImportExportTab'

const TABS = ['Overview', 'Allocation', 'Rooming List', 'Import / Export', 'Access & Deadline', 'Activity Log', 'Settings'] as const
type Tab = (typeof TABS)[number]

export default function CompanyDetail() {
  const { event } = useEvent()
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const [company, setCompany] = useState<CompanyRow | null>(null)
  const [tab, setTab] = useState<Tab>('Overview')
  const { push } = useToast()

  async function load() {
    if (!companyId) return
    const { data, error } = await supabase.from('companies').select('*').eq('id', companyId).maybeSingle()
    if (error) push(error.message, 'error')
    setCompany((data as CompanyRow) || null)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  if (!company || !companyId) return <PageSpinner label="Loading company…" />

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <button className="mb-3 text-xs text-slate-500 hover:text-slate-700" onClick={() => navigate(`/admin/events/${event.id}/companies`)}>
        ← Back to Companies
      </button>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{company.name}</h1>
          <p className="text-sm text-slate-500">{company.code}</p>
        </div>
        <Badge tone="blue">{company.submission_status.replace('_', ' ')}</Badge>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <OverviewTab company={company} onUpdated={load} />}
      {tab === 'Allocation' && <AllocationEditor eventId={event.id} companyId={companyId} onGoToRoomingList={() => setTab('Rooming List')} />}
      {tab === 'Rooming List' && <RoomingListTab companyId={companyId} />}
      {tab === 'Import / Export' && <CompanyImportExportTab eventId={event.id} companyId={companyId} companyName={company.name} companyCode={company.code} />}
      {tab === 'Access & Deadline' && <AccessTab companyId={companyId} />}
      {tab === 'Activity Log' && <ActivityTab companyId={companyId} />}
      {tab === 'Settings' && <SettingsTab company={company} onUpdated={load} />}
    </div>
  )
}

function OverviewTab({ company, onUpdated }: { company: CompanyRow; onUpdated: () => void }) {
  return (
    <div className="max-w-2xl space-y-4">
      <ContactForm company={company} onUpdated={onUpdated} />
    </div>
  )
}

function ContactForm({ company, onUpdated }: { company: CompanyRow; onUpdated: () => void }) {
  const [name, setName] = useState(company.name)
  const [contactPerson, setContactPerson] = useState(company.contact_person || '')
  const [contactMobile, setContactMobile] = useState(company.contact_mobile || '')
  const [contactEmail, setContactEmail] = useState(company.contact_email || '')
  const [notes, setNotes] = useState(company.notes || '')
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  async function save() {
    setSaving(true)
    const { error } = await supabase
      .from('companies')
      .update({ name: name.trim(), contact_person: contactPerson.trim() || null, contact_mobile: contactMobile.trim() || null, contact_email: contactEmail.trim() || null, notes: notes.trim() || null })
      .eq('id', company.id)
    setSaving(false)
    if (error) push(error.message, 'error')
    else {
      push('Saved')
      onUpdated()
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">Company Details</h3>
      <div className="space-y-3">
        <Field label="Company Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Person">
            <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
          </Field>
          <Field label="Contact Mobile">
            <Input value={contactMobile} onChange={(e) => setContactMobile(e.target.value)} />
          </Field>
        </div>
        <Field label="Contact Email">
          <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Button variant="primary" loading={saving} onClick={save}>
          Save
        </Button>
      </div>
    </div>
  )
}

function SettingsTab({ company, onUpdated }: { company: CompanyRow; onUpdated: () => void }) {
  const navigate = useNavigate()
  const { event } = useEvent()
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  async function reopen() {
    const { error } = await supabase.rpc('admin_reopen_submission', { p_company_id: company.id })
    if (error) push(error.message, 'error')
    else {
      push('Rooming list reopened for editing')
      onUpdated()
    }
  }

  async function archive() {
    const ok = await confirm({
      title: `Archive ${company.name}?`,
      description: 'The company disappears from the active list and its portal link stops working. This does not delete any data.',
      tone: 'danger',
      confirmLabel: 'Archive',
    })
    if (!ok) return
    const { error } = await supabase.from('companies').update({ archived_at: new Date().toISOString() }).eq('id', company.id)
    if (error) push(error.message, 'error')
    else {
      push('Company archived')
      navigate(`/admin/events/${event.id}/companies`)
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Submission</h3>
        <p className="mb-3 text-sm text-slate-600">
          Status: <Badge tone="blue">{company.submission_status.replace('_', ' ')}</Badge>
        </p>
        {company.submission_status === 'submitted' && (
          <Button variant="secondary" onClick={reopen}>
            Reopen for Editing
          </Button>
        )}
      </div>
      <div className="rounded-lg border border-red-200 bg-red-50 p-5">
        <h3 className="mb-2 text-sm font-semibold text-red-900">Archive Company</h3>
        <p className="mb-3 text-sm text-red-700">Soft-deletes the company. All rooms, guests and history are preserved but hidden from the active list.</p>
        <Button variant="danger" onClick={archive}>
          Archive Company
        </Button>
      </div>
      {confirmDialog}
    </div>
  )
}
